<template>
  <section class="ap-panel">
    <div class="ap-panel-head">
      <h3>Client List</h3>
      <div style="display:flex;align-items:center;gap:10px">
        <app-badge size="xl" :caps="false" variant="custom" custom-color="#333739" custom-bg="#F2F3F3" :inner-shadow="true">
              <span>{{ filteredClients.length }} clients</span></app-badge>
        <app-button
          variant="custom"
          custom-hover-bg="#b8860b"
          size="sm"
          :icon="['fas', 'download']"
          :disabled="!accountList.length"
          @click="exportCSV"
        >Export CSV</app-button>
      </div>
    </div>

    <!-- Toolbar -->
    <div class="ap-toolbar">
      <!-- Row 1: search + status filters -->
      <div class="ap-toolbar-row">
        <div class="ap-search">
          <fa-icon :icon="['fas', 'search']" class="ap-search-icon" />
          <input
            v-model="searchQuery"
            type="search"
            placeholder="Search by name, email, company…"
            @input="currentPage = 1"
          />
        </div>
        <span class="ap-toolbar-sep mx-3"></span>
        <button
          v-for="f in statusFilters"
          :key="f"
          class="ap-filter-chip"
          :class="{ active: statusFilter === f }"
          @click="setStatusFilter(f)"
        >{{ f }}</button>
      </div>

      <!-- Row 2: category group filters (left) + columns/sort (right) -->
      <div class="ap-toolbar-row">
        <button
          v-for="cg in catGroupFilters"
          :key="cg.key"
          class="ap-filter-chip ap-filter-chip-cat"
          :class="['ap-filter-chip-cat--' + cg.key, { active: !collapsedGroups.includes(cg.key) }]"
          @click="toggleCatGroup(cg.key)"
        >{{ cg.label }}</button>
        <div class="ap-toolbar-right">
          <div class="ap-col-picker" ref="colPicker">
            <button class="ap-filter-chip" @click.stop="showColMenu = !showColMenu">
              <fa-icon :icon="showColMenu ? ['fas', 'times'] : ['fas', 'columns']" />
              {{ showColMenu ? 'Close' : 'Columns' }}
            </button>
            <div v-if="showColMenu" class="ap-col-menu">
              <div class="apcm-kanban">
                <div v-for="group in columnGroups" :key="group.label" class="apcm-lane">
                  <div class="ap-col-menu-group">{{ group.label }}</div>
                  <div class="apcm-lane-body">
                    <label v-for="col in group.cols" :key="col.key" class="ap-col-menu-item">
                      <input type="checkbox" v-model="col.visible" @change="currentPage = 1" />
                      <app-tooltip :text="col.label" position="top" truncate>{{ col.label }}</app-tooltip>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div ref="tableWrap" class="ap-table-wrap" :style="{ height: tableHeight }">
      <table class="ap-table" style="table-layout:fixed;width:max-content" @mouseleave="hoveredColKey = null">
        <colgroup>
          <col style="width:50px;min-width:50px">
          <col v-if="isColVisible('client')"         :style="colStyle('client')">
          <col v-if="isColVisible('initial')"        :style="colStyle('initial')">
          <col v-if="isColVisible('ci_client_type')" :style="colStyle('ci_client_type')">
          <col v-if="isColVisible('ci_year_end')"    :style="colStyle('ci_year_end')">
          <col v-if="isColVisible('ci_services')"         :style="colStyle('ci_services')">
          <col v-if="isColVisible('ci_tax_number')"    :style="colStyle('ci_tax_number')">
          <col v-if="isColVisible('ci_e_number')"      :style="colStyle('ci_e_number')">
          <col v-if="isColVisible('ci_irb_branch')"    :style="colStyle('ci_irb_branch')">
          <col v-if="isColVisible('ci_auditor')"       :style="colStyle('ci_auditor')">
          <col v-if="isColVisible('ci_ssm_new')"       :style="colStyle('ci_ssm_new')">
          <col v-if="isColVisible('ci_ssm_old')"       :style="colStyle('ci_ssm_old')">
          <col v-if="isColVisible('ci_cosec_term_type')" :style="colStyle('ci_cosec_term_type')">
          <col v-if="isColVisible('ci_cosec_term_date')" :style="colStyle('ci_cosec_term_date')">
          <col v-if="isColVisible('status')"         :style="colStyle('status')">
          <col v-for="col in visibleCategoryColDefs" :key="col.key" :style="{ width: col.width + 'px', minWidth: '140px' }">
          <col style="width:120px;min-width:100px">
        </colgroup>
        <thead>
          <tr>
            <th class="ap-num-col ap-sticky-col" style="left:0">#</th>
            <th v-if="isColVisible('client')" class="ap-th-resize ap-sticky-col ap-sticky-col-shadow sortable" style="left:50px" @click="setSort('client')">
              Client
              <fa-icon
                :icon="['fas', sortField === 'client' ? (sortDir === 'asc' ? 'sort-up' : 'sort-down') : 'sort']"
                class="ap-sort-icon"
              />
              <span class="ap-col-resize ap-cat-group-end" @mousedown.stop="startResize($event, 'client')" @click.stop />
            </th>
            <th v-if="isColVisible('initial')" class="ap-th-resize">
              Initial
              <span class="ap-col-resize" @mousedown.stop="startResize($event, 'initial')" />
            </th>
            <th v-if="isColVisible('ci_client_type')" class="ap-th-resize">
              Type
              <span class="ap-col-resize" @mousedown.stop="startResize($event, 'ci_client_type')" />
            </th>
            <th v-if="isColVisible('ci_year_end')" class="ap-th-resize sortable" @click="setSort('ci_year_end')">
              Year End
              <fa-icon
                :icon="['fas', sortField === 'ci_year_end' ? (sortDir === 'asc' ? 'sort-up' : 'sort-down') : 'sort']"
                class="ap-sort-icon"
              />
              <span class="ap-col-resize" @mousedown.stop="startResize($event, 'ci_year_end')" @click.stop />
            </th>
            <th v-if="isColVisible('ci_services')" class="ap-th-resize">
              Services
              <span class="ap-col-resize" @mousedown.stop="startResize($event, 'ci_services')" />
            </th>
            <th v-if="isColVisible('ci_tax_number')" class="ap-th-resize">
              Tax No.
              <span class="ap-col-resize" @mousedown.stop="startResize($event, 'ci_tax_number')" />
            </th>
            <th v-if="isColVisible('ci_e_number')" class="ap-th-resize">
              E Number
              <span class="ap-col-resize" @mousedown.stop="startResize($event, 'ci_e_number')" />
            </th>
            <th v-if="isColVisible('ci_irb_branch')" class="ap-th-resize">
              IRB Branch
              <span class="ap-col-resize" @mousedown.stop="startResize($event, 'ci_irb_branch')" />
            </th>
            <th v-if="isColVisible('ci_auditor')" class="ap-th-resize">
              Auditor
              <span class="ap-col-resize" @mousedown.stop="startResize($event, 'ci_auditor')" />
            </th>
            <th v-if="isColVisible('ci_ssm_new')" class="ap-th-resize">
              SSM (New)
              <span class="ap-col-resize" @mousedown.stop="startResize($event, 'ci_ssm_new')" />
            </th>
            <th v-if="isColVisible('ci_ssm_old')" class="ap-th-resize">
              SSM (Old)
              <span class="ap-col-resize" @mousedown.stop="startResize($event, 'ci_ssm_old')" />
            </th>
            <th v-if="isColVisible('ci_cosec_term_type')" class="ap-th-resize">
              Cosec Term. Type
              <span class="ap-col-resize" @mousedown.stop="startResize($event, 'ci_cosec_term_type')" />
            </th>
            <th v-if="isColVisible('ci_cosec_term_date')" class="ap-th-resize sortable" @click="setSort('ci_cosec_term_date')">
              Cosec Term. Date
              <fa-icon
                :icon="['fas', sortField === 'ci_cosec_term_date' ? (sortDir === 'asc' ? 'sort-up' : 'sort-down') : 'sort']"
                class="ap-sort-icon"
              />
              <span class="ap-col-resize" @mousedown.stop="startResize($event, 'ci_cosec_term_date')" @click.stop />
            </th>
            <th v-if="isColVisible('status')" class="ap-th-resize">
              Status
              <span class="ap-col-resize" @mousedown.stop="startResize($event, 'status')" />
            </th>
            <template v-for="col in categoryColDefs">
              <th v-if="isColVisible(col.key)" :key="col.key" class="ap-th-resize ap-th-cat"
                  :class="{ 'ap-cat-group-start': isFirstVisibleCatInGroup(col) }"
                  :title="col.label">
                <span class="ap-th-cat-label" :style="{ maxWidth: col.width + 'px' }">{{ col.label }}</span>
                <span class="ap-col-resize" @mousedown.stop="startResize($event, col.key)" />
              </th>
            </template>
            <th class="ap-actions-cell"></th>
          </tr>
          <tr class="ap-col-filter-row">
            <th class="ap-sticky-col" style="left:0"><span class="ap-cat-group-bottom"></span></th>
            <th v-if="isColVisible('client')" class="ap-sticky-col ap-sticky-col-shadow" style="left:50px">
              <input v-model="colFilters.client" class="ap-col-filter" placeholder="Filter " />
              <span class="ap-col-resize ap-cat-group-end" @mousedown.stop="startResize($event, 'client')"></span>
              <span class="ap-cat-group-bottom"></span>
            </th>
            <th v-if="isColVisible('initial')"><input v-model="colFilters.initial" class="ap-col-filter" placeholder="Filter" /><span class="ap-cat-group-bottom"></span></th>
            <th v-if="isColVisible('ci_client_type')"><input v-model="colFilters.ci_client_type" class="ap-col-filter" placeholder="e.g. Sdn Bhd" /><span class="ap-cat-group-bottom"></span></th>
            <th v-if="isColVisible('ci_year_end')"><input v-model="colFilters.ci_year_end" class="ap-col-filter" placeholder="e.g. Dec" /><span class="ap-cat-group-bottom"></span></th>
            <th v-if="isColVisible('ci_services')"><input v-model="colFilters.ci_services" class="ap-col-filter" placeholder="e.g. Tax" /><span class="ap-cat-group-bottom"></span></th>
            <th v-if="isColVisible('ci_tax_number')"><input v-model="colFilters.ci_tax_number" class="ap-col-filter" placeholder="Filter" /><span class="ap-cat-group-bottom"></span></th>
            <th v-if="isColVisible('ci_e_number')"><input v-model="colFilters.ci_e_number" class="ap-col-filter" placeholder="Filter" /><span class="ap-cat-group-bottom"></span></th>
            <th v-if="isColVisible('ci_irb_branch')"><input v-model="colFilters.ci_irb_branch" class="ap-col-filter" placeholder="Filter" /><span class="ap-cat-group-bottom"></span></th>
            <th v-if="isColVisible('ci_auditor')"><input v-model="colFilters.ci_auditor" class="ap-col-filter" placeholder="Filter" /><span class="ap-cat-group-bottom"></span></th>
            <th v-if="isColVisible('ci_ssm_new')"><input v-model="colFilters.ci_ssm_new" class="ap-col-filter" placeholder="Filter" /><span class="ap-cat-group-bottom"></span></th>
            <th v-if="isColVisible('ci_ssm_old')"><input v-model="colFilters.ci_ssm_old" class="ap-col-filter" placeholder="Filter" /><span class="ap-cat-group-bottom"></span></th>
            <th v-if="isColVisible('ci_cosec_term_type')"><input v-model="colFilters.ci_cosec_term_type" class="ap-col-filter" placeholder="e.g. Resigned" /><span class="ap-cat-group-bottom"></span></th>
            <th v-if="isColVisible('ci_cosec_term_date')"><input v-model="colFilters.ci_cosec_term_date" class="ap-col-filter" placeholder="e.g. 2025-01" /><span class="ap-cat-group-bottom"></span></th>
            <th v-if="isColVisible('status')"><span class="ap-cat-group-bottom"></span></th>
            <th
              v-for="g in visibleCatGroups"
              :key="g.key"
              :colspan="g.span"
              class="ap-th-cat-group"
              :class="'ap-th-cat-group--' + g.key"
            >
            {{ g.label }}
            <span></span>
            </th>
            <th class="ap-actions-cell">
              <button v-if="hasColFilters" class="ap-col-clear" title="Clear all column filters" @click="clearColFilters">
                <fa-icon :icon="['fas', 'times']" />
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="paginatedClients.length === 0">
            <td :colspan="visibleDataColumns.length + 2" class="ap-empty">No clients found.</td>
          </tr>
          <tr v-for="(client, i) in paginatedClients" :key="client.id || i">
            <td
              class="ap-num-cell ap-sticky-col"
              style="left:0"
              :class="{ 'ap-col-hover': hoveredColKey === '__num' }"
              @mouseenter="hoveredColKey = '__num'"
            >{{ (currentPage - 1) * pageSize + i + 1 }}</td>
            <td
              v-if="isColVisible('client')"
              class="ap-sticky-col ap-sticky-col-shadow"
              style="left:50px"
              :class="{ 'ap-col-hover': hoveredColKey === 'client' }"
              @mouseenter="hoveredColKey = 'client'"
            >
              {{ client.client || '-' }}
              <span class="ap-col-resize ap-cat-group-end" @mousedown.stop="startResize($event, 'client')"></span>
            </td>
            <td
              v-if="isColVisible('initial')"
              :class="{ 'ap-cell-empty': !client.initial, 'ap-col-hover': hoveredColKey === 'initial' }"
              @mouseenter="hoveredColKey = 'initial'"
            >{{ client.initial || '—' }}</td>
            <td
              v-if="isColVisible('ci_client_type')"
              :class="{ 'ap-col-hover': hoveredColKey === 'ci_client_type' }"
              @mouseenter="hoveredColKey = 'ci_client_type'"
            >
              <span v-if="client.company_identity" class="ap-type-badge">
                {{ formatClientType(client.company_identity.client_type) }}
              </span>
              <span v-else class="ap-no-identity">—</span>
            </td>
            <td
              v-if="isColVisible('ci_year_end')"
              :class="{ 'ap-cell-empty': !(client.company_identity && client.company_identity.year_end), 'ap-col-hover': hoveredColKey === 'ci_year_end' }"
              @mouseenter="hoveredColKey = 'ci_year_end'"
            >
              <span v-if="client.company_identity">{{ formatYearEnd(client.company_identity.year_end) }}</span>
              <span v-else>—</span>
            </td>
            <td
              v-if="isColVisible('ci_services')"
              :class="{ 'ap-col-hover': hoveredColKey === 'ci_services' }"
              @mouseenter="hoveredColKey = 'ci_services'"
            >
              <div v-if="client.company_identity && client.company_identity.category" class="ap-svc-list">
                <app-badge v-for="cat in client.company_identity.category"
                  :key="cat.name"
                  :caps="true"
                  size="sm"
                  :services="cat.name"
                  >{{ cat.display_name || cat.name }}</app-badge>
              </div>
              <span v-else>—</span>
            </td>
            <td
              v-if="isColVisible('ci_tax_number')"
              class="ap-mono"
              :class="{ 'ap-cell-empty': !formatTaxNumber(client.company_identity && client.company_identity.tax_number), 'ap-col-hover': hoveredColKey === 'ci_tax_number' }"
              @mouseenter="hoveredColKey = 'ci_tax_number'"
            >{{ formatTaxNumber(client.company_identity && client.company_identity.tax_number) || '—' }}</td>
            <td
              v-if="isColVisible('ci_e_number')"
              class="ap-mono"
              :class="{ 'ap-cell-empty': !formatENumber(client.company_identity && client.company_identity.e_number), 'ap-col-hover': hoveredColKey === 'ci_e_number' }"
              @mouseenter="hoveredColKey = 'ci_e_number'"
            >{{ formatENumber(client.company_identity && client.company_identity.e_number) || '—' }}</td>
            <td
              v-if="isColVisible('ci_irb_branch')"
              :class="{ 'ap-cell-empty': !(client.company_identity && client.company_identity.irb_branch), 'ap-col-hover': hoveredColKey === 'ci_irb_branch' }"
              @mouseenter="hoveredColKey = 'ci_irb_branch'"
            >{{ (client.company_identity && client.company_identity.irb_branch) || '—' }}</td>
            <td
              v-if="isColVisible('ci_auditor')"
              :class="{ 'ap-cell-empty': !(client.company_identity && client.company_identity.auditor), 'ap-col-hover': hoveredColKey === 'ci_auditor' }"
              @mouseenter="hoveredColKey = 'ci_auditor'"
            >{{ (client.company_identity && client.company_identity.auditor) || '—' }}</td>
            <td
              v-if="isColVisible('ci_ssm_new')"
              class="ap-mono"
              :class="{ 'ap-cell-empty': !(client.company_identity && client.company_identity.ssm_number_new), 'ap-col-hover': hoveredColKey === 'ci_ssm_new' }"
              @mouseenter="hoveredColKey = 'ci_ssm_new'"
            >{{ (client.company_identity && client.company_identity.ssm_number_new) || '—' }}</td>
            <td
              v-if="isColVisible('ci_ssm_old')"
              class="ap-mono"
              :class="{ 'ap-cell-empty': !(client.company_identity && client.company_identity.ssm_number_old), 'ap-col-hover': hoveredColKey === 'ci_ssm_old' }"
              @mouseenter="hoveredColKey = 'ci_ssm_old'"
            >{{ (client.company_identity && client.company_identity.ssm_number_old) || '—' }}</td>
            <td
              v-if="isColVisible('ci_cosec_term_type')"
              :class="{ 'ap-col-hover': hoveredColKey === 'ci_cosec_term_type' }"
              @mouseenter="hoveredColKey = 'ci_cosec_term_type'"
            >{{ (client.company_identity && client.company_identity.cosec_termination_type) || '—' }}</td>
            <td
              v-if="isColVisible('ci_cosec_term_date')"
              class="ap-mono"
              :class="{ 'ap-col-hover': hoveredColKey === 'ci_cosec_term_date' }"
              @mouseenter="hoveredColKey = 'ci_cosec_term_date'"
            >{{ (client.company_identity && client.company_identity.cosec_termination_date) || '—' }}</td>
            <td
              v-if="isColVisible('status')"
              :class="{ 'ap-col-hover': hoveredColKey === 'status' }"
              @mouseenter="hoveredColKey = 'status'"
            >
              <app-badge>{{ client.status || 'Active' }}</app-badge>
            </td>
            <template v-for="col in categoryColDefs">
              <td
                v-if="isColVisible(col.key)"
                :key="col.key"
                class="ap-cat-cell"
                :class="{
                  'ap-cat-group-start': isFirstVisibleCatInGroup(col),
                  'ap-cell-empty': col.key === 'ci__pay_mo__payday' && !getCatColValue(client, col) && (client.company_identity?.category || []).some(cat => cat.name === 'payroll_monthly'),
                  'ap-col-hover': hoveredColKey === col.key
                }"
                @mouseenter="hoveredColKey = col.key"
              >
                <span
                  v-if="col.type === 'status'"
                  class="ap-ci-status"
                  :class="'ap-ci-status-' + (getCatColValue(client, col) || 'na')"
                >{{ formatCatColValue(getCatColValue(client, col), 'status') }}</span>
                <span v-else>{{ formatCatColValue(getCatColValue(client, col), 'date') }}</span>
              </td>
            </template>
            <td
              class="ap-actions-cell"
              :class="{ 'ap-col-hover': hoveredColKey === '__actions' }"
              @mouseenter="hoveredColKey = '__actions'"
            >
              <div class="ap-row-actions">
                <app-tooltip text="view" position="right">
                  <button class="ap-row-action ap-row-action-view" title="View" @click="$emit('view', client)">
                    <fa-icon :icon="['fas', 'eye']" />
                  </button>
                </app-tooltip>
                <app-tooltip text="edit" position="right">
                  <button class="ap-row-action ap-row-action-edit" title="Edit" @click="$router.push({ name: 'Admin-Edit-Client', params: { lang: $route.params.lang, id: client.id } })">
                    <fa-icon :icon="['fas', 'edit']" />
                  </button>
                </app-tooltip>
                <app-tooltip text="delete" position="left">
                  <button class="ap-row-action ap-row-action-danger" title="Remove" @click="$emit('remove', client)">
                    <fa-icon :icon="['fas', 'trash']" />
                  </button>
                </app-tooltip>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div class="ap-table-foot">
      <div class="ap-table-info">
        Showing <b>{{ pageFrom }}</b>–<b>{{ pageTo }}</b> of <b>{{ filteredClients.length }}</b>
      </div>
      <div class="ap-pager">
        <button :disabled="currentPage <= 1" @click="currentPage--">
          <fa-icon :icon="['fas', 'chevron-left']" />
        </button>
        <button
          v-for="p in visiblePages"
          :key="p"
          :class="{ active: p === currentPage }"
          :disabled="p === '…'"
          @click="typeof p === 'number' && (currentPage = p)"
        >{{ p }}</button>
        <button :disabled="currentPage >= totalPages" @click="currentPage++">
          <fa-icon :icon="['fas', 'chevron-right']" />
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import AppTooltip from '@/components/shared/AppTooltip.vue'
import AppBadge from '../shared/AppBadge.vue'
import AppButton from '../shared/AppButton.vue'

// Defines all company_identity deadline/status columns.
// path[1] = direct sub_category item; path[2] = sub_category → tasks item
const CAT_COL_DEFS = [
  // ── Tax ──────────────────────────────────────────────────────────────
  { key: 'ci__tax__tax_filing', label: 'Tax Filing', cat: 'tax', path: ['tax_filing'], type: 'date', group: 'tax', width: 88 },
  { key: 'ci__tax__mitrs', label: 'MITRS', cat: 'tax', path: ['mitrs'], type: 'date', group: 'tax', width: 88 },
  { key: 'ci__tax__cp204', label: 'CP204', cat: 'tax', path: ['cp204'], type: 'date', group: 'tax', width: 88 },
  { key: 'ci__tax__cp204a6', label: 'CP204A(6)', cat: 'tax', path: ['cp204a6'], type: 'date', group: 'tax', width: 88 },
  { key: 'ci__tax__cp204a9', label: 'CP204A(9)', cat: 'tax', path: ['cp204a9'], type: 'date', group: 'tax', width: 88 },
  { key: 'ci__tax__cp204a11', label: 'CP204A(11)', cat: 'tax', path: ['cp204a11'], type: 'date', group: 'tax', width: 88 },
  // ── TP Reminder ───────────────────────────────────────────────────────
  { key: 'ci__tp__tp_reminder', label: 'TP Reminder', cat: 'tp_reminder', path: ['tp_reminder'], type: 'date', group: 'tp_reminder', width: 115 },
  // ── Cosec ────────────────────────────────────────────────────────────
  { key: 'ci__cosec__ar', label: 'AR', cat: 'cosec', path: ['compliance', 'ar'], type: 'date', group: 'cosec', width: 88 },
  { key: 'ci__cosec__fs', label: 'FS', cat: 'cosec', path: ['compliance', 'fs'], type: 'date', group: 'cosec', width: 88 },
  { key: 'ci__cosec__ar_reminder_1st', label: 'AR Reminder 1', cat: 'cosec', path: ['reminder', 'ar_reminder_1st'], type: 'date', group: 'cosec', width: 102 },
  { key: 'ci__cosec__ar_reminder_2nd', label: 'AR Reminder 2', cat: 'cosec', path: ['reminder', 'ar_reminder_2nd'], type: 'date', group: 'cosec', width: 102 },
  { key: 'ci__cosec__ar_reminder_late_1', label: 'AR Late 1', cat: 'cosec', path: ['reminder', 'ar_reminder_late_1'], type: 'date', group: 'cosec', width: 88 },
  { key: 'ci__cosec__ar_reminder_late_2', label: 'AR Late 2', cat: 'cosec', path: ['reminder', 'ar_reminder_late_2'], type: 'date', group: 'cosec', width: 88 },
  { key: 'ci__cosec__afs_reminder_1st', label: 'AFS Reminder 1', cat: 'cosec', path: ['reminder', 'afs_reminder_1st'], type: 'date', group: 'cosec', width: 105 },
  { key: 'ci__cosec__afs_reminder_2nd', label: 'AFS Reminder 2', cat: 'cosec', path: ['reminder', 'afs_reminder_2nd'], type: 'date', group: 'cosec', width: 105 },
  { key: 'ci__cosec__afs_reminder_late_1', label: 'AFS Late 1', cat: 'cosec', path: ['reminder', 'afs_reminder_late_1'], type: 'date', group: 'cosec', width: 88 },
  { key: 'ci__cosec__afs_reminder_late_2', label: 'AFS Late 2', cat: 'cosec', path: ['reminder', 'afs_reminder_late_2'], type: 'date', group: 'cosec', width: 88 },
  // ── Payroll Yearly ───────────────────────────────────────────────────
  { key: 'ci__pay_yr__form_ea', label: 'Form EA', cat: 'payroll_yearly', path: ['form_ea'], type: 'date', group: 'payroll_yearly', width: 88 },
  { key: 'ci__pay_yr__form_e', label: 'Form E', cat: 'payroll_yearly', path: ['form_e'], type: 'date', group: 'payroll_yearly', width: 88 },
  // ── Payroll Reminder ─────────────────────────────────────────────────
  { key: 'ci__pay_rm__reminder', label: 'PR Reminder', cat: 'payroll_reminder', path: ['payroll_reminder'], type: 'date', group: 'payroll_reminder', width: 100 },
  // ── Payroll Monthly ──────────────────────────────────────────────────
  { key: 'ci__pay_mo__payslip', label: 'Payslip', cat: 'payroll_monthly', path: ['payslip'], type: 'date', group: 'payroll_monthly', width: 88 },
  { key: 'ci__pay_mo__pcb', label: 'PCB/EPF/SOCSO/EIS/HRDC', cat: 'payroll_monthly', path: ['pcb_epf_socso_eis_hrdc'], type: 'date', group: 'payroll_monthly', width: 95 },
  { key: 'ci__pay_mo__payday', label: 'Payday', cat: 'payroll_monthly', path: ['payday'], type: 'date', group: 'payroll_monthly', width: 88 },
  // ── Accounting ───────────────────────────────────────────────────────
  { key: 'ci__acct__sst1', label: 'SST 1', cat: 'accounting', path: ['sst1'], type: 'date', group: 'accounting', width: 88 },
  { key: 'ci__acct__sst2', label: 'SST 2', cat: 'accounting', path: ['sst2'], type: 'date', group: 'accounting', width: 88 },
  { key: 'ci__acct__sst3', label: 'SST 3', cat: 'accounting', path: ['sst3'], type: 'date', group: 'accounting', width: 88 },
  { key: 'ci__acct__sst4', label: 'SST 4', cat: 'accounting', path: ['sst4'], type: 'date', group: 'accounting', width: 88 },
  { key: 'ci__acct__sst5', label: 'SST 5', cat: 'accounting', path: ['sst5'], type: 'date', group: 'accounting', width: 88 },
  { key: 'ci__acct__sst6', label: 'SST 6', cat: 'accounting', path: ['sst6'], type: 'date', group: 'accounting', width: 88 }
]

export default {
  name: 'admin-client-table',
  components: { AppTooltip, AppBadge, AppButton },
  props: {
    accountList: { type: Array, default: () => [] }
  },
  emits: ['view', 'edit', 'remove', 'filtered-count-change'],
  data () {
    return {
      columns: [],
      columnConfig: {
        client: { label: 'Client', width: 180 },
        initial: { label: 'Initial', width: 90 },
        ci_client_type: { label: 'Type', width: 110 },
        ci_year_end: { label: 'Year End', width: 100 },
        ci_services: { label: 'Services', width: 220 },
        ci_tax_number: { label: 'Tax No.', width: 130, visible: false },
        ci_e_number: { label: 'E Number', width: 120, visible: false },
        ci_irb_branch: { label: 'IRB Branch', width: 140, visible: false },
        ci_auditor: { label: 'Auditor', width: 140, visible: false },
        ci_ssm_new: { label: 'SSM (New)', width: 130, visible: false },
        ci_ssm_old: { label: 'SSM (Old)', width: 120, visible: false },
        ci_cosec_term_type: { label: 'Cosec Term. Type', width: 140, visible: false },
        ci_cosec_term_date: { label: 'Cosec Term. Date', width: 130, visible: false },
        status: { label: 'Status', width: 110 }
      },
      tableHeight: 'auto',
      showColMenu: false,
      hoveredColKey: null,
      searchQuery: '',
      statusFilter: 'All',
      statusFilters: ['All', 'Active', 'Pending', 'Terminated'],
      collapsedGroups: [],
      sortField: 'client',
      sortDir: 'asc',
      catGroupFilters: [
        { key: 'tax', label: 'Tax' },
        { key: 'tp_reminder', label: 'TP Reminder' },
        { key: 'cosec', label: 'Cosec' },
        { key: 'payroll_yearly', label: 'Payroll Yearly' },
        { key: 'payroll_reminder', label: 'Payroll Reminder' },
        { key: 'payroll_monthly', label: 'Payroll Monthly' },
        { key: 'accounting', label: 'Accounting' }
      ],
      currentPage: 1,
      pageSize: 8,
      colFilters: {
        id: '',
        client: '',
        initial: '',
        ci_client_type: '',
        ci_year_end: '',
        ci_services: '',
        ci_tax_number: '',
        ci_e_number: '',
        ci_irb_branch: '',
        ci_auditor: '',
        ci_ssm_new: '',
        ci_ssm_old: '',
        ci_cosec_term_type: '',
        ci_cosec_term_date: ''
      }
    }
  },
  computed: {
    filteredClients () {
      const q = this.searchQuery.trim().toLowerCase()
      const cf = this.colFilters
      return this.accountList.filter(c => {
        if (this.statusFilter !== 'All') {
          if (!c.status || c.status.toLowerCase() !== this.statusFilter.toLowerCase()) return false
        }
        if (q) {
          const hit = (
            (c.full_name || '').toLowerCase().includes(q) ||
            (c.email || '').toLowerCase().includes(q) ||
            (c.client || '').toLowerCase().includes(q) ||
            String(c.id).includes(q)
          )
          if (!hit) return false
        }
        if (cf.id.trim() && !String(c.id).includes(cf.id.trim())) return false
        if (cf.client.trim() && !(c.client || '').toLowerCase().includes(cf.client.trim().toLowerCase())) return false
        if (cf.initial.trim() && !(c.initial || '').toLowerCase().includes(cf.initial.trim().toLowerCase())) return false
        if (cf.ci_client_type.trim()) {
          const v = cf.ci_client_type.trim().toLowerCase()
          const t = this.formatClientType(c.company_identity?.client_type).toLowerCase()
          if (!t.includes(v)) return false
        }
        if (cf.ci_year_end.trim()) {
          const v = cf.ci_year_end.trim().toLowerCase()
          const ye = this.formatYearEnd(c.company_identity?.year_end).toLowerCase()
          if (!ye.includes(v)) return false
        }
        if (cf.ci_services.trim()) {
          const v = cf.ci_services.trim().toLowerCase()
          const names = (c.company_identity?.category || []).map(cat => (cat.display_name || cat.name).toLowerCase()).join(' ')
          if (!names.includes(v)) return false
        }
        if (cf.ci_tax_number.trim() && !this.formatTaxNumber(c.company_identity?.tax_number).toLowerCase().includes(cf.ci_tax_number.trim().toLowerCase())) return false
        if (cf.ci_e_number.trim() && !this.formatENumber(c.company_identity?.e_number).toLowerCase().includes(cf.ci_e_number.trim().toLowerCase())) return false
        if (cf.ci_irb_branch.trim() && !(c.company_identity?.irb_branch || '').toLowerCase().includes(cf.ci_irb_branch.trim().toLowerCase())) return false
        if (cf.ci_auditor.trim() && !(c.company_identity?.auditor || '').toLowerCase().includes(cf.ci_auditor.trim().toLowerCase())) return false
        if (cf.ci_ssm_new.trim() && !(c.company_identity?.ssm_number_new || '').toLowerCase().includes(cf.ci_ssm_new.trim().toLowerCase())) return false
        if (cf.ci_ssm_old.trim() && !(c.company_identity?.ssm_number_old || '').toLowerCase().includes(cf.ci_ssm_old.trim().toLowerCase())) return false
        if (cf.ci_cosec_term_type.trim() && !(c.company_identity?.cosec_termination_type || '').toLowerCase().includes(cf.ci_cosec_term_type.trim().toLowerCase())) return false
        if (cf.ci_cosec_term_date.trim() && !(c.company_identity?.cosec_termination_date || '').includes(cf.ci_cosec_term_date.trim())) return false
        return true
      })
    },
    sortedClients () {
      if (!this.sortField) return this.filteredClients
      const arr = [...this.filteredClients]
      const dir = this.sortDir === 'asc' ? 1 : -1
      arr.sort((a, b) => {
        if (this.sortField === 'client') {
          const va = (a.client || '').toLowerCase()
          const vb = (b.client || '').toLowerCase()
          return va < vb ? -dir : va > vb ? dir : 0
        }
        if (this.sortField === 'ci_year_end') {
          const va = a.company_identity?.year_end ?? null
          const vb = b.company_identity?.year_end ?? null
          if (va === null && vb === null) return 0
          if (va === null) return 1
          if (vb === null) return -1
          return (va - vb) * dir
        }
        if (this.sortField === 'ci_cosec_term_date') {
          const va = a.company_identity?.cosec_termination_date ?? null
          const vb = b.company_identity?.cosec_termination_date ?? null
          if (va === null && vb === null) return 0
          if (va === null) return 1
          if (vb === null) return -1
          return va < vb ? -dir : va > vb ? dir : 0
        }
        return 0
      })
      return arr
    },
    paginatedClients () {
      const start = (this.currentPage - 1) * this.pageSize
      return this.sortedClients.slice(start, start + this.pageSize)
    },
    totalPages () { return Math.max(1, Math.ceil(this.filteredClients.length / this.pageSize)) },
    pageFrom () {
      if (!this.filteredClients.length) return 0
      return (this.currentPage - 1) * this.pageSize + 1
    },
    pageTo () { return Math.min(this.currentPage * this.pageSize, this.filteredClients.length) },
    visiblePages () {
      const total = this.totalPages
      const cur = this.currentPage
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
      const pages = [1]
      if (cur > 3) pages.push('…')
      const start = Math.max(2, cur - 1)
      const end = Math.min(total - 1, cur + 1)
      for (let i = start; i <= end; i++) pages.push(i)
      if (cur < total - 2) pages.push('…')
      pages.push(total)
      return pages
    },
    hasColFilters () { return Object.values(this.colFilters).some(v => v.trim() !== '') },
    visibleDataColumns () { return this.columns.filter(c => c.visible) },
    categoryColDefs () { return this.columns.filter(c => c.isCatCol) },
    visibleCategoryColDefs () { return this.columns.filter(c => c.isCatCol && c.visible && !this.collapsedGroups.includes(c.group)) },
    visibleCatGroups () {
      const LABELS = { tax: 'Tax', tp_reminder: 'TP Reminder', cosec: 'Cosec', payroll_yearly: 'Payroll Yearly', payroll_reminder: 'Payroll Reminder', payroll_monthly: 'Payroll Monthly', accounting: 'Accounting' }
      const ORDER = ['tax', 'tp_reminder', 'cosec', 'payroll_yearly', 'payroll_reminder', 'payroll_monthly', 'accounting']
      return ORDER
        .map(g => ({ key: g, label: LABELS[g], span: this.visibleCategoryColDefs.filter(c => c.group === g).length }))
        .filter(g => g.span > 0)
    },
    columnGroups () {
      const buckets = {}
      for (const col of this.columns) {
        const g = col.group || 'General'
        if (!buckets[g]) buckets[g] = []
        buckets[g].push(col)
      }
      const ORDER = ['General', 'tax', 'tp_reminder', 'cosec', 'payroll_yearly', 'payroll_reminder', 'payroll_monthly', 'accounting']
      const LABELS = { General: 'General', tax: 'Tax', tp_reminder: 'TP Reminder', cosec: 'Cosec', payroll_yearly: 'Payroll Yearly', payroll_reminder: 'Payroll Reminder', payroll_monthly: 'Payroll Monthly', accounting: 'Accounting' }
      return ORDER.filter(k => buckets[k]).map(k => ({ label: LABELS[k], cols: buckets[k] }))
    }
  },
  watch: {
    accountList () { this.buildColumns(); this.$nextTick(this.computeTableHeight) },
    filteredClients: {
      immediate: true,
      handler (val) {
        this.$emit('filtered-count-change', val.length)
        this.currentPage = 1
      }
    },
    colFilters: { deep: true, handler () { this.currentPage = 1 } }
  },
  mounted () {
    this.buildColumns()
    this.$nextTick(this.computeTableHeight)
    document.addEventListener('click', this.onColPickerOutside)
    window.addEventListener('resize', this.computeTableHeight)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.onColPickerOutside)
    document.removeEventListener('mousemove', this.onResize)
    document.removeEventListener('mouseup', this.stopResize)
    window.removeEventListener('resize', this.computeTableHeight)
  },
  methods: {
    computeTableHeight () {
      const wrap = this.$refs.tableWrap
      if (!wrap) return
      this.tableHeight = 'auto'
      this.$nextTick(() => {
        if (!this.$refs.tableWrap) return
        const w = this.$refs.tableWrap
        const h = w.scrollHeight
        this.tableHeight = `${h < 600 ? h + 15 : 600}px`
        const firstRow = w.querySelector('thead tr:first-child')
        if (firstRow) w.style.setProperty('--ap-filter-row-top', firstRow.offsetHeight + 'px')
      })
    },
    buildColumns () {
      const sample = this.accountList[0] || {}
      const hasIdentity = this.accountList.some(c => c.company_identity)
      const base = Object.keys(this.columnConfig)
        .filter(k => {
          if (k.startsWith('ci_')) return hasIdentity
          return k in sample
        })
        .map(k => ({
          key: k,
          label: this.columnConfig[k].label,
          width: this.columnConfig[k].width,
          sortable: !!this.columnConfig[k].sortable,
          visible: this.columnConfig[k].visible !== false,
          group: 'General'
        }))
      const catCols = hasIdentity
        ? CAT_COL_DEFS.map(d => ({ ...d, visible: true, isCatCol: true, width: Math.max(d.width, 140) }))
        : []
      this.columns = [...base, ...catCols]
    },
    isColVisible (key) {
      const col = this.columns.find(c => c.key === key)
      if (!col) return true
      if (col.group && col.group !== 'General' && this.collapsedGroups.includes(col.group)) return false
      return col.visible
    },
    colStyle (key) {
      const col = this.columns.find(c => c.key === key)
      return col ? { width: col.width + 'px', minWidth: '60px' } : {}
    },
    startResize (e, key) {
      const col = this.columns.find(c => c.key === key)
      if (!col) return
      e.preventDefault()
      this._resizeCol = col
      this._resizeStartX = e.clientX
      this._resizeStartW = col.width
      document.addEventListener('mousemove', this.onResize)
      document.addEventListener('mouseup', this.stopResize)
    },
    onResize (e) {
      if (!this._resizeCol) return
      const minW = this._resizeCol.isCatCol ? 140 : 60
      this._resizeCol.width = Math.max(minW, this._resizeStartW + (e.clientX - this._resizeStartX))
    },
    stopResize () {
      this._resizeCol = null
      document.removeEventListener('mousemove', this.onResize)
      document.removeEventListener('mouseup', this.stopResize)
    },
    onColPickerOutside (e) {
      if (this.$refs.colPicker && !this.$refs.colPicker.contains(e.target)) this.showColMenu = false
    },
    toggleCatGroup (group) {
      const idx = this.collapsedGroups.indexOf(group)
      if (idx === -1) this.collapsedGroups.push(group)
      else this.collapsedGroups.splice(idx, 1)
    },
    isFirstVisibleCatInGroup (col) {
      const first = this.visibleCategoryColDefs.find(d => d.group === col.group)
      return first && first.key === col.key
    },
    setStatusFilter (f) { this.statusFilter = f; this.currentPage = 1 },
    setSort (field) {
      if (this.sortField === field) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortField = field
        this.sortDir = 'asc'
      }
      this.currentPage = 1
    },
    getCatColValue (client, colDef) {
      const ci = client.company_identity
      if (!ci || !ci.category) return null
      const cat = ci.category.find(c => c.name === colDef.cat)
      if (!cat) return null
      const [p0, p1] = colDef.path
      if (!p1) {
        // flat sub_category item (tax, accounting)
        const item = (cat.sub_category || []).find(s => s.name === p0)
        if (!item) return null
        return item.date !== undefined ? item.date : (item.status ?? null)
      }
      // nested: sub_category → tasks (cosec, payroll)
      const sub = (cat.sub_category || []).find(s => s.name === p0)
      if (!sub) return null
      const task = (sub.tasks || []).find(t => t.name === p1)
      if (!task) return null
      return task.date !== undefined ? task.date : (task.status ?? null)
    },
    formatCatColValue (val, type) {
      if (val === null || val === undefined) return '—'
      if (type === 'status') {
        const map = { pending: 'Pending', completed: 'Completed', na: 'N/A' }
        return map[String(val).toLowerCase()] || val
      }
      if (val && typeof val === 'object') {
        if (val.day && val.month) {
          const M = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
          return `${val.day} ${M[val.month - 1]}`
        }
        if (val.month) return `Day ${val.month}`
        if (val.day) return `Day ${val.day}`
      }
      return '—'
    },
    formatTaxNumber (tn) {
      if (!tn) return ''
      if (typeof tn === 'object') return (tn.group || '') + (tn.number || '')
      return tn
    },
    formatENumber (en) {
      if (!en) return ''
      if (typeof en === 'object') return (en.prefixed || '') + (en.number || '')
      return en
    },
    formatClientType (type) {
      const map = { sdn_bhd: 'Sdn Bhd', llp: 'LLP', sole_prop: 'Sole Prop', partnership: 'Partnership', salaried_person: 'Salaried' }
      return map[type] || type || '—'
    },
    formatYearEnd (month) {
      if (!month) return '—'
      return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][month - 1] || month
    },
    avatarColor (i) {
      const palette = ['#b8860b', '#6a4f36', '#333739', '#5b7a5a', '#7094c8', '#b35d40', '#8a6a9c', '#3d6f6c']
      return palette[i % palette.length]
    },
    initials (name) {
      if (!name) return '?'
      return name.split(' ').slice(0, 2).map(s => s[0]).join('').toUpperCase()
    },
    clearColFilters () {
      Object.keys(this.colFilters).forEach(k => { this.colFilters[k] = '' })
    },
    exportCSV () {
      if (!this.filteredClients.length) return
      const catHeaders = CAT_COL_DEFS.map(d => d.label)
      const headers = [
        'ID', 'Full Name', 'Email', 'Client', 'Initial', 'Type', 'Year End', 'Services',
        'Tax No.', 'E Number', 'IRB Branch', 'Auditor', 'SSM (New)', 'SSM (Old)',
        'Cosec Term. Type', 'Cosec Term. Date',
        'Status', ...catHeaders
      ]
      const rows = this.filteredClients.map(c => {
        const ci = c.company_identity
        const services = (ci?.category || []).map(cat => cat.display_name || cat.name).join(', ')
        const catVals = CAT_COL_DEFS.map(d => this.formatCatColValue(this.getCatColValue(c, d), d.type))
        return [
          c.id, c.full_name, c.email, c.client || '', c.initial || '',
          ci ? this.formatClientType(ci.client_type) : '',
          ci ? this.formatYearEnd(ci.year_end) : '',
          services,
          this.formatTaxNumber(ci?.tax_number), this.formatENumber(ci?.e_number), ci?.irb_branch || '', ci?.auditor || '',
          ci?.ssm_number_new || '', ci?.ssm_number_old || '',
          ci?.cosec_termination_type || '', ci?.cosec_termination_date || '',
          c.status || 'Active',
          ...catVals
        ]
      })
      const csv = [headers, ...rows]
        .map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
        .join('\n')
      const a = document.createElement('a')
      a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
      a.download = `clients-${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
      URL.revokeObjectURL(a.href)
    }
  }
}
</script>

<style scoped>
/* ── Columns picker layout ───────────────────────────────────────── */
/* Override global .ap-col-menu constraints */
.ap-col-menu {
  max-height: 420px !important;
  overflow-y: auto !important;
  display: block !important;
  width: min(860px, 90vw) !important;
  scrollbar-width: none; /* Firefox */
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12), 0 1px 4px rgba(0, 0, 0, 0.08);
}

.ap-col-menu::-webkit-scrollbar {
  display: none; /* Chrome / Safari / Edge */
}

/* Groups stacked as full-width rows */
.apcm-kanban {
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* One group row */
.apcm-lane {
  padding: 12px;
  border-bottom: 1px solid #e5e7eb;
}

.apcm-lane:last-child {
  border-bottom: none;
}

/* Group heading */
.apcm-lane .ap-col-menu-group {
  text-decoration: underline;
  padding: 0;
  margin-bottom: 6px;
}

/* Items flow left-to-right, 5 per row */
.apcm-lane-body {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
}

.apcm-lane .ap-col-menu-item input[type="checkbox"] {
  width: 13px;
  height: 13px;
  flex-shrink: 0;
}

/* ── Category column header truncation ───────────────────────────── */
/* The span's max-width is bound to col.width so ellipsis tracks     */
/* the live column width as the user drags to resize.                */
.ap-th-cat-label {
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* ── Crosshair (+) hover ──────────────────────────────────────────── */

/* Column arm: highlight every cell in the hovered column */
.ap-table tbody td.ap-col-hover {
  background-color: rgba(184, 134, 11, 0.07);
}

/* Row arm: standard row hover — reinforce it so it beats the base cell bg */
.ap-table tbody tr:hover td {
  background-color: rgba(184, 134, 11, 0.07);
}

/* Intersection cell (hovered row + hovered column) — stronger accent */
.ap-table tbody tr:hover td.ap-col-hover {
  background-color: rgba(184, 134, 11, 0.18);
}

/* Keep sticky cells consistent — they carry their own background */
.ap-table tbody td.ap-sticky-col.ap-col-hover,
.ap-table tbody tr:hover td.ap-sticky-col {
  background-color: color-mix(in srgb, var(--ap-bg, #fff) 88%, rgba(184, 134, 11, 0.45));
}
.ap-table tbody tr:hover td.ap-sticky-col.ap-col-hover {
  background-color: color-mix(in srgb, var(--ap-bg, #fff) 76%, rgba(184, 134, 11, 0.55));
}
</style>
